import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Mail, Phone, Calendar, User, MapPin, UserCheck, Shield, CreditCard, AlertCircle, CheckCircle, Edit2, Save, X, Loader2, UserMinus, UserPlus, Banknote, Car, Images, Download } from 'lucide-react';
import { formatSwissPhone, getPhoneLink, isPhoneField, isEmailField } from '@/utils/formatters';
import { VehicleGallery } from '@/components/VehicleGallery';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MemberDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin, impersonate, impersonating } = useAuth();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [impersonateLoading, setImpersonateLoading] = useState(false);
  const [adminToggleLoading, setAdminToggleLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editedFields, setEditedFields] = useState({});
  const [editedEmail, setEditedEmail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  
  // Jahresbeitrag state
  const [editingJahresbeitrag, setEditingJahresbeitrag] = useState(false);
  const [jahresbeitragValue, setJahresbeitragValue] = useState('');

  const [savingJahresbeitrag, setSavingJahresbeitrag] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setMember(null);
    setVehicles([]);
    setEditing(false);
    setEditedFields({});

    const fetchMember = async () => {
      try {
        const token = localStorage.getItem('shrt_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API_URL}/api/members/${id}`, {
          headers, signal: controller.signal
        });
        setMember(response.data);
        
        // Also fetch vehicles
        try {
          const vehiclesResponse = await axios.get(`${API_URL}/api/members/${id}/vehicles`, {
            headers, signal: controller.signal
          });
          setVehicles(vehiclesResponse.data.vehicles || []);
        } catch (vehicleErr) {
          if (!axios.isCancel(vehicleErr)) console.log('Could not fetch vehicles');
        }
      } catch (err) {
        if (axios.isCancel(err)) return;
        setError(err.response?.data?.detail || 'Mitglied nicht gefunden');
        if (err.response?.status !== 404) {
          toast.error('Fehler beim Laden des Mitglieds');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchMember();
    return () => controller.abort();
  }, [id]);

  const handleImpersonate = async () => {
    setImpersonateLoading(true);
    const result = await impersonate(member.id);
    if (result.success) {
      toast.success(`Sie sehen jetzt die Ansicht als ${member.name}`);
      navigate('/members');
    } else {
      toast.error(result.error);
    }
    setImpersonateLoading(false);
  };

  const handleToggleAdmin = async () => {
    setAdminToggleLoading(true);
    try {
      const token = localStorage.getItem('shrt_token');
      const response = await axios.put(
        `${API_URL}/api/admin/members/${member.id}/admin-status`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMember(prev => ({ ...prev, is_admin: response.data.is_admin }));
      toast.success(response.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Ändern des Admin-Status');
    } finally {
      setAdminToggleLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    const newStatus = member?.status === 'ausgetreten' ? 'aktiv' : 'ausgetreten';
    const confirmMessage = newStatus === 'ausgetreten' 
      ? `Möchten Sie ${member?.name} wirklich als ausgetreten markieren? Das Mitglied erhält dann keine Rechnungen mehr.`
      : `Möchten Sie ${member?.name} wieder als aktives Mitglied markieren?`;
    
    if (!window.confirm(confirmMessage)) return;
    
    setStatusLoading(true);
    try {
      const token = localStorage.getItem('shrt_token');
      const response = await axios.put(
        `${API_URL}/api/admin/members/${member.id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMember(prev => ({ 
        ...prev, 
        status: newStatus,
        is_active: newStatus === 'aktiv'
      }));
      toast.success(response.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Ändern des Status');
    } finally {
      setStatusLoading(false);
    }
  };

  // Handle Jahresbeitrag update
  const handleSaveJahresbeitrag = async () => {
    setSavingJahresbeitrag(true);
    try {
      const token = localStorage.getItem('shrt_token');
      const value = jahresbeitragValue ? parseFloat(jahresbeitragValue) : null;
      
      const response = await axios.put(
        `${API_URL}/api/admin/members/${id}/jahresbeitrag`,
        null,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { jahresbeitrag: value }
        }
      );
      
      setMember(response.data.member);
      setEditingJahresbeitrag(false);
      toast.success(response.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingJahresbeitrag(false);
    }
  };

  const startEditJahresbeitrag = () => {
    setJahresbeitragValue(member?.custom_jahresbeitrag?.toString() || '');
    setEditingJahresbeitrag(true);
  };

  // Standard fields that should always be shown when editing
  const standardFields = [
    'Vorname',
    'Name',
    'Strasse',
    'PLZ',
    'Ort',
    'Tel. Privat',
    'Tel. Geschäft',
    'Mobile',
    'Fax',
    'Email'
  ];

  // Start editing mode
  const handleStartEdit = () => {
    const initialFields = {};
    
    // First, add all standard fields (empty if not present)
    standardFields.forEach(fieldName => {
      const existingValue = member?.fields?.[fieldName]?.text || '';
      initialFields[fieldName] = existingValue;
    });
    
    // Then add any additional fields from member.fields that aren't in standardFields
    if (member?.fields) {
      Object.entries(member.fields).forEach(([key, field]) => {
        if (!standardFields.includes(key)) {
          initialFields[key] = field.text || '';
        }
      });
    }
    
    setEditedFields(initialFields);
    setEditing(true);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditing(false);
    setEditedFields({});
    setEditedEmail(null);
  };

  // Update a field value
  const handleFieldChange = (fieldName, value) => {
    setEditedFields(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Save changes and sync to Monday.com
  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('shrt_token');
      
      // Find changed fields (including newly added fields)
      const changes = [];
      Object.entries(editedFields).forEach(([fieldName, newValue]) => {
        const oldValue = member.fields?.[fieldName]?.text || '';
        // Include if value changed OR if it's a new field with a value
        if (newValue !== oldValue && (newValue || oldValue)) {
          changes.push({
            field_name: fieldName,
            old_value: oldValue,
            new_value: newValue
          });
        }
      });

      // Check if email was changed
      const emailChanged = editedEmail !== null && editedEmail !== member.email;
      
      if (changes.length === 0 && !emailChanged) {
        toast.info('Keine Änderungen gefunden');
        setEditing(false);
        return;
      }

      // If email changed, add it as a field change so the backend syncs it
      if (emailChanged) {
        changes.push({
          field_name: 'E-Mail',
          old_value: member.email,
          new_value: editedEmail
        });
      }

      // Send changes to backend
      const response = await axios.put(
        `${API_URL}/api/admin/members/${member.id}/fields`,
        { changes },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      const updatedFields = { ...member.fields };
      changes.forEach(change => {
        if (change.field_name === 'E-Mail') return; // handled separately
        if (change.new_value) {
          updatedFields[change.field_name] = {
            ...updatedFields[change.field_name],
            text: change.new_value,
            type: updatedFields[change.field_name]?.type || 'text'
          };
        } else if (updatedFields[change.field_name]) {
          delete updatedFields[change.field_name];
        }
      });
      
      setMember(prev => ({ 
        ...prev, 
        fields: updatedFields,
        ...(emailChanged ? { email: editedEmail } : {})
      }));
      setEditing(false);
      setEditedFields({});
      setEditedEmail(null);
      
      toast.success(response.data.message || `${changes.length} Änderungen gespeichert und zu Monday.com synchronisiert`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Generate and download VCF (vCard) file
  const handleDownloadVCF = () => {
    if (!member) return;
    
    const fields = member.fields || {};
    
    // Extract name parts
    const nameParts = (member.name || '').split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    // Find phone numbers
    let phone = '';
    let mobile = '';
    Object.entries(fields).forEach(([key, field]) => {
      const lower = key.toLowerCase();
      const value = field?.text || '';
      if (!value) return;
      
      if (lower.includes('mobile') || lower.includes('handy') || lower.includes('natel')) {
        mobile = value.replace(/\s/g, '');
      } else if (lower.includes('phone') || lower.includes('telefon') || lower.includes('tel')) {
        phone = value.replace(/\s/g, '');
      }
    });
    
    // Find address parts
    let street = '';
    let city = '';
    let zip = '';
    let country = 'Schweiz';
    Object.entries(fields).forEach(([key, field]) => {
      const lower = key.toLowerCase();
      const value = field?.text || '';
      if (!value) return;
      
      if (lower.includes('strasse') || lower.includes('street') || lower.includes('adresse')) {
        street = value;
      } else if (lower.includes('ort') || lower.includes('city') || lower.includes('stadt')) {
        city = value;
      } else if (lower.includes('plz') || lower.includes('zip') || lower.includes('postleitzahl')) {
        zip = value;
      } else if (lower.includes('land') || lower.includes('country')) {
        country = value;
      }
    });
    
    // Build vCard
    const vcfEmailField = Object.entries(fields).find(([k]) => k.toLowerCase().includes('mail'));
    const vcfEmail = vcfEmailField?.[1]?.text || member.email;
    
    const vcard = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `N:${lastName};${firstName};;;`,
      `FN:${member.name}`,
      `ORG:S.H.R.T. - Swiss Historic Racing Team`,
      vcfEmail ? `EMAIL;TYPE=INTERNET:${vcfEmail}` : '',
      phone ? `TEL;TYPE=WORK:${phone}` : '',
      mobile ? `TEL;TYPE=CELL:${mobile}` : '',
      (street || city || zip) ? `ADR;TYPE=HOME:;;${street};${city};;${zip};${country}` : '',
      'END:VCARD'
    ].filter(line => line).join('\r\n');
    
    // Create and download file
    const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${member.name.replace(/\s+/g, '_')}.vcf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Kontakt heruntergeladen');
  };

  const getFieldIcon = (fieldId) => {
    const lower = fieldId.toLowerCase();
    if (lower.includes('email') || lower.includes('e-mail') || lower.includes('mail')) return Mail;
    if (lower.includes('phone') || lower.includes('telefon') || lower.includes('tel') || lower.includes('mobile') || lower.includes('handy') || lower.includes('fax')) return Phone;
    if (lower.includes('date') || lower.includes('datum')) return Calendar;
    if (lower.includes('address') || lower.includes('adresse') || lower.includes('ort') || lower.includes('strasse') || lower.includes('plz')) return MapPin;
    return User;
  };

  const renderFieldValue = (fieldName, value) => {
    if (!value) return <span className="text-muted-foreground">—</span>;
    
    // Phone fields - format and make clickable
    if (isPhoneField(fieldName)) {
      const formatted = formatSwissPhone(value);
      const link = getPhoneLink(value);
      return (
        <a 
          href={link} 
          className="text-foreground hover:text-primary transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          {formatted}
        </a>
      );
    }
    
    // Email fields - make clickable
    if (isEmailField(fieldName) || value.includes('@')) {
      return (
        <a 
          href={`mailto:${value}`} 
          className="text-foreground hover:text-primary transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          {value}
        </a>
      );
    }
    
    return <span className="text-foreground">{value}</span>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-48 bg-secondary/50" />
            <div className="bg-card border border-border p-8">
              <div className="flex items-center gap-6 mb-8">
                <div className="w-24 h-24 bg-secondary/50" />
                <div className="space-y-2">
                  <div className="h-6 w-48 bg-secondary/50" />
                  <div className="h-4 w-64 bg-secondary/50" />
                </div>
              </div>
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-16 bg-secondary/50" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="">
          <div className="bg-card border border-border p-8 text-center">
            <p className="text-destructive mb-4">{error}</p>
            <Button
              onClick={() => navigate('/members')}
              variant="outline"
              className="rounded-none"
            >
              <ArrowLeft className="mr-2" size={18} />
              Zurück zur Liste
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  // Group fields by type for better organization
  const contactFields = [];
  const otherFields = [];
  
  // Fields that should only be visible to admins
  const adminOnlyFields = [
    'zahlungspflichtig',
    'jahresbeitrag', 
    '2026', '2027', '2028',
    'bezahlt am',
    'zahlart',
    'monday'
  ];

  // When editing, use editedFields; otherwise use member.fields
  const fieldsToDisplay = editing ? editedFields : (member?.fields || {});

  if (fieldsToDisplay) {
    // If editing, iterate over editedFields keys
    // If not editing, iterate over member.fields entries
    const entries = editing 
      ? Object.entries(editedFields).map(([key, value]) => [key, { text: value, type: 'text' }])
      : Object.entries(member?.fields || {});
    
    entries.forEach(([key, field]) => {
      const lower = key.toLowerCase();
      
      // Skip admin-only fields for non-admin users
      if (!isAdmin && adminOnlyFields.some(f => lower.includes(f))) {
        return;
      }
      
      const isContact = lower.includes('phone') || 
                       lower.includes('telefon') || 
                       lower.includes('tel.') ||
                       lower.includes('tel ') ||
                       lower.includes('mobile') ||
                       lower.includes('handy') ||
                       lower.includes('fax') ||
                       lower.includes('natel');
      
      // Skip email fields from fields - we use member.email as single source
      const isEmailField = lower.includes('email') || lower.includes('e-mail') || lower.includes('mail');
      if (isEmailField) return;
      
      // When editing, show all fields; when not editing, only show fields with values
      const hasValue = editing || field.text;
      
      if (isContact && hasValue) {
        contactFields.push({ key, ...field });
      } else if (hasValue) {
        otherFields.push({ key, ...field });
      }
    });
  }

  return (
    <Layout>
      <div className="animate-slide-in overflow-x-hidden">
        {/* Back Button */}
        <div className="mb-6">
          <Link
            to="/members"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm uppercase tracking-wider"
            data-testid="back-to-members"
          >
            <ArrowLeft size={16} />
            Zurück zur Liste
          </Link>
        </div>

        {/* Member Card */}
        <div className="bg-card border border-border overflow-hidden">
          {/* Header with gradient */}
          <div 
            className="h-24 sm:h-32 relative"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1727163806308-9622d1b5e4a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxzd2lzcyUyMGFscHMlMjB3aW5kaW5nJTIwcm9hZHxlbnwwfHx8fDE3NjkyMDMwNzl8MA&ixlib=rb-4.1.0&q=85')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
          </div>

          {/* Profile Info */}
          <div className="px-4 sm:px-8 pb-6 sm:pb-8 -mt-12 sm:-mt-16 relative">
            <div className="flex flex-col gap-4 sm:gap-6 mb-6 sm:mb-8">
              {/* Avatar and Name */}
              <div className="flex items-end gap-3 sm:gap-6">
                <div className="w-16 h-16 sm:w-24 sm:h-24 bg-secondary flex items-center justify-center border-4 border-card flex-shrink-0">
                  <span className="font-['Oswald'] text-xl sm:text-3xl font-bold text-foreground">
                    {getInitials(member?.name)}
                  </span>
                </div>
                <div className="min-w-0 flex-1 overflow-hidden">
                  <h1 className="font-['Oswald'] text-lg sm:text-2xl md:text-3xl font-bold text-foreground uppercase tracking-tight truncate">
                    {member?.name}
                  </h1>
                  <p className="text-muted-foreground text-xs sm:text-base truncate">{member?.email}</p>
                  <div className="flex items-center gap-1.5 sm:gap-2 mt-2 flex-wrap">
                    {member?.status === 'ausgetreten' ? (
                      <span className="inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-red-500/20 text-red-500 text-[10px] sm:text-xs uppercase tracking-wider">
                        <UserMinus size={10} className="sm:w-3 sm:h-3" />
                        Ausgetreten
                      </span>
                    ) : (
                      <span className={`status-badge ${member?.is_active ? 'active' : 'inactive'} text-[10px] sm:text-xs`}>
                        {member?.is_active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    )}
                    {member?.is_admin && (
                      <span className="inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-primary/20 text-primary text-[10px] sm:text-xs uppercase tracking-wider">
                        <Shield size={10} className="sm:w-3 sm:h-3" />
                        Admin
                      </span>
                    )}
                    {/* VCF Download Button */}
                    <button
                      onClick={handleDownloadVCF}
                      className="inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-secondary hover:bg-secondary/80 text-foreground text-[10px] sm:text-xs uppercase tracking-wider transition-colors"
                      title="Als Kontakt speichern"
                      data-testid="download-vcf-btn"
                    >
                      <Download size={10} className="sm:w-3 sm:h-3" />
                      <span className="hidden sm:inline">Kontakt speichern</span>
                      <span className="sm:hidden">VCF</span>
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Admin Actions */}
              {isAdmin && !impersonating && (
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4">
                  {/* Edit Button */}
                  {!editing ? (
                    <Button
                      onClick={handleStartEdit}
                      variant="outline"
                      className="uppercase tracking-widest font-bold text-xs sm:text-sm rounded-none w-full sm:w-auto"
                      data-testid="edit-member-btn"
                    >
                      <Edit2 className="mr-2" size={16} />
                      Bearbeiten
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={handleSaveChanges}
                        disabled={saving}
                        className="bg-green-600 hover:bg-green-700 text-white uppercase tracking-widest font-bold text-xs sm:text-sm rounded-none w-full sm:w-auto"
                        data-testid="save-changes-btn"
                      >
                        {saving ? (
                          <Loader2 className="mr-2 animate-spin" size={16} />
                        ) : (
                          <Save className="mr-2" size={16} />
                        )}
                        Speichern & Sync
                      </Button>
                      <Button
                        onClick={handleCancelEdit}
                        variant="outline"
                        className="uppercase tracking-widest font-bold text-xs sm:text-sm rounded-none w-full sm:w-auto"
                        data-testid="cancel-edit-btn"
                      >
                        <X className="mr-2" size={16} />
                        Abbrechen
                      </Button>
                    </>
                  )}
                  
                  {/* Admin Toggle - only for other users */}
                  {!editing && user?.id !== member?.id && (
                    <Button
                      onClick={handleToggleAdmin}
                      disabled={adminToggleLoading}
                      variant={member?.is_admin ? "destructive" : "outline"}
                      className="uppercase tracking-widest font-bold text-xs sm:text-sm rounded-none w-full sm:w-auto"
                      data-testid="toggle-admin-btn"
                    >
                      {adminToggleLoading ? (
                        <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Shield className="mr-2" size={16} />
                          <span className="hidden sm:inline">{member?.is_admin ? 'Admin entfernen' : 'Zum Admin machen'}</span>
                          <span className="sm:hidden">{member?.is_admin ? 'Admin entf.' : 'Zum Admin'}</span>
                        </>
                      )}
                    </Button>
                  )}
                  
                  {/* Impersonate Button - only for non-admins */}
                  {!editing && !member?.is_admin && user?.id !== member?.id && (
                    <Button
                      onClick={handleImpersonate}
                      disabled={impersonateLoading}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black uppercase tracking-widest font-bold text-xs sm:text-sm rounded-none w-full sm:w-auto"
                      data-testid="impersonate-btn"
                    >
                      {impersonateLoading ? (
                        <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <UserCheck className="mr-2" size={16} />
                          <span className="hidden sm:inline">Als Benutzer anmelden</span>
                          <span className="sm:hidden">Anmelden als</span>
                        </>
                      )}
                    </Button>
                  )}
                  
                  {/* Status Toggle Button - Mark as resigned */}
                  {!editing && user?.id !== member?.id && (
                    <Button
                      onClick={handleToggleStatus}
                      disabled={statusLoading}
                      variant={member?.status === 'ausgetreten' ? "default" : "destructive"}
                      className={`uppercase tracking-widest font-bold text-xs sm:text-sm rounded-none w-full sm:w-auto ${
                        member?.status === 'ausgetreten' ? 'bg-green-600 hover:bg-green-700' : ''
                      }`}
                      data-testid="toggle-status-btn"
                    >
                      {statusLoading ? (
                        <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      ) : member?.status === 'ausgetreten' ? (
                        <>
                          <UserPlus className="mr-2" size={16} />
                          <span className="hidden sm:inline">Wieder aktivieren</span>
                          <span className="sm:hidden">Aktivieren</span>
                        </>
                      ) : (
                        <>
                          <UserMinus className="mr-2" size={16} />
                          <span className="hidden sm:inline">Als ausgetreten markieren</span>
                          <span className="sm:hidden">Ausgetreten</span>
                        </>
                      )}
                    </Button>
                  )}
                </div>
              )}
              
              {/* Jahresbeitrag Section - Admin Only */}
              {isAdmin && !impersonating && (
                <div className="mt-6 p-4 bg-secondary/30 border border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Banknote className="text-primary" size={20} />
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wider">Jahresbeitrag</p>
                        {editingJahresbeitrag ? (
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-sm">CHF</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={jahresbeitragValue}
                              onChange={(e) => setJahresbeitragValue(e.target.value)}
                              placeholder="Standard"
                              className="w-32 h-8 bg-background"
                            />
                          </div>
                        ) : (
                          <p className="text-foreground font-semibold">
                            {member?.custom_jahresbeitrag ? (
                              <span>CHF {member.custom_jahresbeitrag.toFixed(2)} <span className="text-xs text-muted-foreground">(individuell)</span></span>
                            ) : (
                              <span className="text-muted-foreground">Standard</span>
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {editingJahresbeitrag ? (
                        <>
                          <Button
                            onClick={handleSaveJahresbeitrag}
                            disabled={savingJahresbeitrag}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 rounded-none"
                          >
                            {savingJahresbeitrag ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                          </Button>
                          <Button
                            onClick={() => setEditingJahresbeitrag(false)}
                            variant="outline"
                            size="sm"
                            className="rounded-none"
                          >
                            <X size={14} />
                          </Button>
                        </>
                      ) : (
                        <Button
                          onClick={startEditJahresbeitrag}
                          variant="outline"
                          size="sm"
                          className="rounded-none"
                        >
                          <Edit2 size={14} className="mr-1" />
                          Ändern
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Contact Info */}
            {(member?.email || contactFields.length > 0) && (
              <div className="mb-6 sm:mb-8">
                <h2 className="font-['Oswald'] text-base sm:text-lg font-bold text-foreground uppercase tracking-tight mb-3 sm:mb-4 border-b border-border pb-2">
                  Kontakt
                </h2>
                <div className="grid grid-cols-1 gap-3">
                  {/* Email - single source of truth: member.email */}
                  {member?.email && (
                    <div className="flex items-center gap-3 p-3 sm:p-4 bg-secondary/30">
                      <Mail className="text-primary flex-shrink-0" size={18} />
                      <div className="min-w-0 flex-1 overflow-hidden">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider truncate">
                          E-Mail
                        </p>
                        {editing ? (
                          <Input
                            type="email"
                            value={editedEmail ?? member.email}
                            onChange={(e) => setEditedEmail(e.target.value)}
                            className="mt-1 bg-background"
                            data-testid="edit-email-input"
                          />
                        ) : (
                          <a href={`mailto:${member.email}`} className="text-primary hover:underline text-sm sm:text-base break-all">
                            {member.email}
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                  {contactFields.map((field) => {
                    const Icon = getFieldIcon(field.key);
                    return (
                      <div key={field.key} className="flex items-center gap-3 p-3 sm:p-4 bg-secondary/30">
                        <Icon className="text-primary flex-shrink-0" size={18} />
                        <div className="min-w-0 flex-1 overflow-hidden">
                          <p className="text-xs text-muted-foreground uppercase tracking-wider truncate">
                            {field.key}
                          </p>
                          {editing ? (
                            <Input
                              type="text"
                              value={editedFields[field.key] ?? field.text ?? ''}
                              onChange={(e) => handleFieldChange(field.key, e.target.value)}
                              className="mt-1 bg-background"
                            />
                          ) : (
                            <div className="break-all text-sm sm:text-base">
                              {renderFieldValue(field.key, field.text)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Other Fields */}
            {otherFields.length > 0 && (
              <div>
                <h2 className="font-['Oswald'] text-base sm:text-lg font-bold text-foreground uppercase tracking-tight mb-3 sm:mb-4 border-b border-border pb-2">
                  Weitere Informationen
                </h2>
                <div className="space-y-0">
                  {otherFields.map((field) => {
                    const isZahlungspflichtig = field.key.toLowerCase().includes('zahlungspflichtig');
                    return (
                    <div key={field.key} className="py-3 px-2 border-b border-border/50 last:border-0">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{field.key}</p>
                      <div className="text-sm sm:text-base break-words">
                        {editing ? (
                          isZahlungspflichtig ? (
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={['ja', 'zahlungspflichtig', 'true', '1', 'yes'].includes(
                                  (editedFields[field.key] ?? field.text ?? '').toString().toLowerCase()
                                )}
                                onChange={(e) => handleFieldChange(field.key, e.target.checked ? 'Zahlungspflichtig' : 'Nicht zahlungspflichtig')}
                                className="w-4 h-4 accent-primary"
                                data-testid="zahlungspflichtig-checkbox"
                              />
                              <span className="text-sm">Zahlungspflichtig</span>
                            </label>
                          ) : (
                          <Input
                            type="text"
                            value={editedFields[field.key] ?? field.text ?? ''}
                            onChange={(e) => handleFieldChange(field.key, e.target.value)}
                            className="bg-background"
                          />
                          )
                        ) : (
                          renderFieldValue(field.key, field.text)
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Vehicles Section */}
            {vehicles.length > 0 && (
              <div className="mt-8">
                <h2 className="font-['Oswald'] text-lg font-bold text-foreground uppercase tracking-tight mb-4 border-b border-border pb-2 flex items-center gap-2">
                  <Car className="text-primary" size={20} />
                  Fahrzeuge
                  <span className="text-xs font-normal text-muted-foreground ml-2">
                    ({vehicles.length} {vehicles.length === 1 ? 'Fahrzeug' : 'Fahrzeuge'})
                  </span>
                </h2>
                <div className="space-y-6">
                  {vehicles.map((vehicle) => (
                    <Link 
                      key={vehicle.id} 
                      to={`/fahrzeug/${id}/${vehicle.id}`}
                      className="bg-secondary/20 border border-border overflow-hidden block hover:border-primary/50 transition-colors"
                      data-testid={`member-vehicle-${vehicle.id}`}
                    >
                      {/* Vehicle Header */}
                      <div className="flex items-start gap-3 p-4 border-b border-border/50">
                        <Car className="text-primary flex-shrink-0 mt-0.5" size={20} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-foreground">{vehicle.name}</p>
                            {vehicle.year && (
                              <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary border border-primary/20">
                                {vehicle.year}
                              </span>
                            )}
                            {vehicle.images && vehicle.images.length > 0 && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Images size={12} />
                                {vehicle.images.length} Bilder
                              </span>
                            )}
                          </div>
                          {vehicle.description && (
                            <p className="text-sm text-muted-foreground mt-1">{vehicle.description}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Vehicle Gallery */}
                      {vehicle.images && vehicle.images.length > 0 && (
                        <div className="p-4">
                          <VehicleGallery 
                            images={vehicle.images}
                            vehicleName={vehicle.name}
                            canEdit={false}
                          />
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Admin-Only: Billing Information */}
            {isAdmin && (
              <div className="mt-8">
                <h2 className="font-['Oswald'] text-lg font-bold text-foreground uppercase tracking-tight mb-4 border-b border-border pb-2 flex items-center gap-2">
                  <CreditCard className="text-primary" size={20} />
                  Abrechnungsinformationen
                  <span className="text-xs font-normal text-muted-foreground ml-2">(nur für Admins)</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Zahlungspflichtig Status */}
                  <div className="flex items-center gap-3 p-4 bg-secondary/30">
                    <div className={`p-2 rounded-full ${member?.zahlungspflichtig ? 'bg-amber-500/20 text-amber-500' : 'bg-muted text-muted-foreground'}`}>
                      <CreditCard size={20} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">
                        Zahlungspflichtig
                      </p>
                      <p className="text-foreground font-medium">
                        {member?.zahlungspflichtig ? (
                          <span className="flex items-center gap-1 text-amber-500">
                            <AlertCircle size={14} />
                            Ja
                          </span>
                        ) : (
                          <span className="text-muted-foreground">Nein</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* 2026 Payment Status */}
                  <div className="flex items-center gap-3 p-4 bg-secondary/30">
                    <div className={`p-2 rounded-full ${member?.payment_status_2026 === 'bezahlt' ? 'bg-green-500/20 text-green-500' : member?.payment_status_2026 === 'offen' ? 'bg-red-500/20 text-red-500' : 'bg-muted text-muted-foreground'}`}>
                      {member?.payment_status_2026 === 'bezahlt' ? (
                        <CheckCircle size={20} />
                      ) : member?.payment_status_2026 === 'offen' ? (
                        <AlertCircle size={20} />
                      ) : (
                        <CreditCard size={20} />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">
                        2026
                      </p>
                      <p className="text-foreground font-medium">
                        {member?.payment_status_2026 === 'bezahlt' ? (
                          <span className="flex items-center gap-1 text-green-500">
                            <CheckCircle size={14} />
                            Bezahlt
                          </span>
                        ) : member?.payment_status_2026 === 'offen' ? (
                          <span className="flex items-center gap-1 text-red-500">
                            <AlertCircle size={14} />
                            Offen
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* 2027 Payment Status */}
                  <div className="flex items-center gap-3 p-4 bg-secondary/30">
                    <div className={`p-2 rounded-full ${member?.payment_status_2027 === 'bezahlt' ? 'bg-green-500/20 text-green-500' : member?.payment_status_2027 === 'offen' ? 'bg-red-500/20 text-red-500' : 'bg-muted text-muted-foreground'}`}>
                      {member?.payment_status_2027 === 'bezahlt' ? (
                        <CheckCircle size={20} />
                      ) : member?.payment_status_2027 === 'offen' ? (
                        <AlertCircle size={20} />
                      ) : (
                        <CreditCard size={20} />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">
                        2027
                      </p>
                      <p className="text-foreground font-medium">
                        {member?.payment_status_2027 === 'bezahlt' ? (
                          <span className="flex items-center gap-1 text-green-500">
                            <CheckCircle size={14} />
                            Bezahlt
                          </span>
                        ) : member?.payment_status_2027 === 'offen' ? (
                          <span className="flex items-center gap-1 text-red-500">
                            <AlertCircle size={14} />
                            Offen
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* 2028 Payment Status */}
                  <div className="flex items-center gap-3 p-4 bg-secondary/30">
                    <div className={`p-2 rounded-full ${member?.payment_status_2028 === 'bezahlt' ? 'bg-green-500/20 text-green-500' : member?.payment_status_2028 === 'offen' ? 'bg-red-500/20 text-red-500' : 'bg-muted text-muted-foreground'}`}>
                      {member?.payment_status_2028 === 'bezahlt' ? (
                        <CheckCircle size={20} />
                      ) : member?.payment_status_2028 === 'offen' ? (
                        <AlertCircle size={20} />
                      ) : (
                        <CreditCard size={20} />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">
                        2028
                      </p>
                      <p className="text-foreground font-medium">
                        {member?.payment_status_2028 === 'bezahlt' ? (
                          <span className="flex items-center gap-1 text-green-500">
                            <CheckCircle size={14} />
                            Bezahlt
                          </span>
                        ) : member?.payment_status_2028 === 'offen' ? (
                          <span className="flex items-center gap-1 text-red-500">
                            <AlertCircle size={14} />
                            Offen
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Monday.com ID - Admin only */}
            {isAdmin && member?.monday_id && (
              <div className="mt-8 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Monday.com ID: <span className="font-mono">{member?.monday_id}</span>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
